import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, storage } from '../../pages/app/protected/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export default function CompanyProfile() {
  const { user } = useAuth();
  const [companyId, setCompanyId] = useState('');
  const [formData, setFormData] = useState({
    companyName: '',
    regType: 'Business Registration No.',
    regNumber: '',
    oldRegNumber: '',
    tinNumber: '',
    sstNumber: '',
    phone: '',
    countryCode: '+60',
    email: '',
    website: '',
    street: '',
    postcode: '',
    city: '',
    state: '',
    country: '',
    logoUrl: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loadingPage, setLoadingPage] = useState(false);

  const countryCodes = ['+60', '+65', '+62', '+66'];
  const countries = ['Malaysia', 'Singapore', 'Indonesia', 'Thailand'];
  const registrationTypes = [
    'BRN',
    'NRIC',
    'Passport',
    'Army'
  ];

  useEffect(() => {
    const fetchProfile = async () => {
      setLoadingPage(true);
      if (!user) return;
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists() || !userDoc.data().companyId) {
        throw new Error('No company associated with this user');
      }
      const companyId = userDoc.data().companyId;
      setCompanyId(companyId);
      const ref = doc(db, 'companies', companyId, 'companyProfile', 'info');
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setFormData(prev => ({ ...prev, ...snap.data() }));
        setPreviewUrl(snap.data().logoUrl || '');
      }
      setLoadingPage(false);
    };
    fetchProfile();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const cleanedValue = value.replace(/^0+/, '');
    setFormData(prev => ({ ...prev, phone: cleanedValue }));
  };
  const handleRegTypeChange = (type) => {
    setFormData(prev => ({ ...prev, regType: type }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    setMessage('');
    try {
      // Get company ID first
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists() || !userDoc.data().companyId) {
        throw new Error('No company associated with this user');
      }
      const companyId = userDoc.data().companyId;

      let logoUrl = formData.logoUrl;
      if (logoFile) {
        const storageRef = ref(storage, `companies/${companyId}/companyLogo/${logoFile.name}`);
        await uploadBytes(storageRef, logoFile);
        logoUrl = await getDownloadURL(storageRef);
      }
      await setDoc(doc(db, 'companies', companyId, 'companyProfile', 'info'), {
        ...formData,
        logoUrl
      });
      setMessage('Company profile updated!');
      setLogoFile(null);
    } catch (err) {
      console.error(err);
      setError('Failed to save');
    } finally {
      setLoading(false);
    }
  };

  if (loadingPage) {
    return (
      <div className="w-full flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
        <span className="text-white">Loading company information...</span>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl text-white mx-auto p-8 rounded-2xl shadow-md">
      <h2 className="text-3xl font-bold mb-8 text-white">Company Profile</h2>

      {/* Logo */}
      <div className="flex items-center gap-6 mb-8">
        <div className="w-24 h-24 rounded-full overflow-hidden bg-[#1f1f1f]">
          {previewUrl ? (
            <img src={previewUrl} alt="Company Logo" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500">No Logo</div>
          )}
        </div>
        <div>
          <label className="block text-sm mb-2 text-gray-400">Upload Company Logo</label>
          <input type="file" accept="image/*" onChange={handleLogoChange} className="text-white" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm mb-2 text-gray-400">Company Name <span className="text-red-500">*</span></label>
          <input name="companyName" required value={formData.companyName} onChange={handleChange} className="w-full bg-[#1f1f1f] rounded-lg px-4 py-3" />
        </div>

        <div>
          <label className="block text-sm mb-2 text-gray-400">Registration Type <span className="text-red-500">*</span></label>
          <div className="flex flex-wrap gap-2">
            {registrationTypes.map(type => (
              <button
                key={type}
                required onClick={() => handleRegTypeChange(type)}
                className={`px-4 py-2 rounded-lg border text-sm ${formData.regType === type ? 'bg-purple-600 text-white' : 'bg-[#1f1f1f] text-gray-300 border-gray-600'}`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm mb-2 text-gray-400">Registration Number / Other Type <span className="text-red-500">*</span></label>
          <input name="regNumber" placeholder="e.g. 220031920391" required value={formData.regNumber} onChange={handleChange} className="w-full bg-[#1f1f1f] rounded-lg px-4 py-3" />
        </div>

        <div>
          <label className="block text-sm mb-2 text-gray-400">Old Registration Number</label>
          <input name="oldRegNumber" placeholder="e.g. 0291029-H" value={formData.oldRegNumber} onChange={handleChange} className="w-full bg-[#1f1f1f] rounded-lg px-4 py-3" />
        </div>

        <div>
          <label className="block text-sm mb-2 text-gray-400">TIN Number</label>
          <input name="tinNumber" placeholder="e.g. IG0000000000" value={formData.tinNumber} onChange={handleChange} className="w-full bg-[#1f1f1f] rounded-lg px-4 py-3" />
        </div>

        <div>
          <label className="block text-sm mb-2 text-gray-400">SST Number</label>
          <input name="sstNumber" placeholder="e.g. STM-YYMM-XXXXXXXXXX" value={formData.sstNumber} onChange={handleChange} className="w-full bg-[#1f1f1f] rounded-lg px-4 py-3" />
        </div>

        <div>
          <label className="block text-sm mb-2 text-gray-400">Phone Number <span className="text-red-500" required>*</span></label>
          <div className="flex gap-2">
            <select name="countryCode" value={formData.countryCode} onChange={handleChange} className="bg-[#1f1f1f] rounded-lg px-4 py-3">
              {countryCodes.map(code => <option key={code} value={code}>{code}</option>)}
            </select>
            <input name="phone" value={formData.phone} onChange={handlePhoneChange} className="flex-1 bg-[#1f1f1f] rounded-lg px-4 py-3" />
          </div>
        </div>

        <div>
          <label className="block text-sm mb-2 text-gray-400">Email <span className="text-red-500">*</span></label>
          <input name="email" required value={formData.email} onChange={handleChange} className="w-full bg-[#1f1f1f] rounded-lg px-4 py-3" />
        </div>

        <div>
          <label className="block text-sm mb-2 text-gray-400">Website</label>
          <input name="website" value={formData.website} onChange={handleChange} className="w-full bg-[#1f1f1f] rounded-lg px-4 py-3" />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm mb-2 text-gray-400">Street Address <span className="text-red-500">*</span></label>
          <textarea name="street" value={formData.street} onChange={handleChange} className="w-full bg-[#1f1f1f] rounded-lg px-4 py-3" rows="3" />
        </div>

        <div>
          <label className="block text-sm mb-2 text-gray-400">Postcode <span className="text-red-500">*</span></label>
          <input name="postcode" required value={formData.postcode} onChange={handleChange} className="w-full bg-[#1f1f1f] rounded-lg px-4 py-3" />
        </div>

        <div>
          <label className="block text-sm mb-2 text-gray-400">City <span className="text-red-500">*</span></label>
          <input name="city" required value={formData.city} onChange={handleChange} className="w-full bg-[#1f1f1f] rounded-lg px-4 py-3" />
        </div>

        <div>
          <label className="block text-sm mb-2 text-gray-400">State <span className="text-red-500">*</span></label>
          <input name="state" required value={formData.state} onChange={handleChange} className="w-full bg-[#1f1f1f] rounded-lg px-4 py-3" />
        </div>

        <div>
          <label className="block text-sm mb-2 text-gray-400">Country <span className="text-red-500">*</span></label>
          <select name="country" required value={formData.country} onChange={handleChange} className="w-full bg-[#1f1f1f] rounded-lg px-4 py-3">
            <option value="">Select Country</option>
            {countries.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-10">
        <button
          onClick={handleSave}
          disabled={loading || !formData.companyName || !formData.regNumber || !formData.countryCode || !formData.phone || !formData.email || !formData.street || !formData.postcode || !formData.city || !formData.state || !formData.country}
          className="bg-purple-600 hover:bg-purple-700 px-6 py-3 rounded-lg font-bold text-white disabled:opacity-50"
        >
          {loading  ? 'Saving...' : 'Save Changes'}
        </button>
        {message && <p className="mt-4 text-green-400 font-semibold">{message}</p>}
        {error && <p className="mt-4 text-red-400 font-semibold">{error}</p>}
      </div>
    </div>
  );
}